import React from 'react';

/**
 * App-level error boundary. Catches React render/lifecycle errors that would
 * otherwise unmount the entire React tree (leaving only the html background
 * visible — looks like a "blank green page" on WINLUXOR).
 *
 * Shows a user-friendly recovery UI + the actual error so we can debug.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log to console for the developer
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] caught:', error, info);
    this.setState({ info });

    // Report to backend so admins can see client-side crashes in the admin
    // dashboard. Failure is silent so the error UI still renders.
    try {
      const backend = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('winluxor_token') || '';
      fetch(`${backend}/api/client-errors/report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          error_name: String(error?.name || 'Error'),
          error_message: String(error?.message || error),
          stack: String(error?.stack || '').slice(0, 4000),
          component_stack: String(info?.componentStack || '').slice(0, 4000),
          url: window.location.href,
          user_agent: navigator.userAgent,
          ts: new Date().toISOString(),
        }),
      }).catch(() => {});
    } catch {
      /* ignore */
    }
  }

  handleReload = () => {
    try {
      this.setState({ hasError: false, error: null, info: null });
    } catch {
      // ignore
    }
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const { error, info } = this.state;
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-cream">
        <div className="max-w-xl w-full bg-white rounded-2xl border-2 border-red-200 shadow-2xl p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 mx-auto mb-4 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-red-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-rolex-green mb-2" data-testid="error-boundary-title">
            Une erreur est survenue
          </h1>
          <p className="text-rolex-green/70 text-sm mb-6">
            La page a rencontré un problème inattendu. Vos données sont en sécurité.
          </p>
          {error && (
            <details className="text-left bg-stone-50 border border-stone-200 rounded-lg p-3 mb-5 text-xs font-mono text-rolex-green/80">
              <summary className="cursor-pointer font-semibold text-rolex-green">Détails techniques</summary>
              <p className="mt-2 break-words"><strong>{String(error.name || 'Error')}:</strong> {String(error.message || error)}</p>
              {info?.componentStack && (
                <pre className="mt-2 whitespace-pre-wrap text-[10px] opacity-70 max-h-40 overflow-auto">
                  {info.componentStack}
                </pre>
              )}
            </details>
          )}
          <div className="flex gap-3 justify-center flex-wrap">
            <button
              onClick={this.handleReload}
              className="px-5 py-2.5 rounded-xl bg-gold-gradient text-rolex-green font-bold text-sm hover:opacity-90 transition-opacity"
              data-testid="error-boundary-reload"
            >
              Recharger la page
            </button>
            <button
              onClick={this.handleGoHome}
              className="px-5 py-2.5 rounded-xl border border-rolex-green/30 text-rolex-green font-bold text-sm hover:bg-rolex-green/5 transition-colors"
              data-testid="error-boundary-home"
            >
              Retour à l'accueil
            </button>
          </div>
        </div>
      </div>
    );
  }
}
